import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  Box,
  Button,
  Text,
  HStack,
  useColorModeValue,
  InputGroup,
  Input,
  InputRightElement,
  FormLabel,
  SimpleGrid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
} from "@chakra-ui/react";
import { CalendarIcon } from "@chakra-ui/icons";
import { formatDateToDDMONTHNAMEYYYY } from "utils/common";
import ErrorMessage from "./ErrorMessage";

const CalendarInput = ({
  value,
  onChange,
  name,
  label,
  isRequired,
  error,
  disablePastDate = true,
}) => {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [date, setDate] = useState(value);
  const today = new Date();

  useEffect(() => {
    if (selectedPreset) {
      setDate(selectedPreset);
    }
  }, [selectedPreset]);

  useEffect(() => {
    if (value) {
      setDate(new Date(value));
    }
  }, [value]);

  const onDateChange = (value) => {
    if (value <= today && disablePastDate) {
      value = today;
    }
    setSelectedPreset(null);
    setDate(value);
  };

  const applyFilter = (onClose) => {
    const customEvent = {
      target: {
        name,
        value: date.toISOString(),
      },
    };
    onChange(customEvent);
    onClose();
  };

  const handleCustomDateChange = (days) => {
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + days);
    setSelectedPreset(newDate);
  };

  const calendarBg = useColorModeValue("white", "gray.700");
  const calendarBorderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <Box>
      <FormLabel htmlFor={name}>
        {label}
        {isRequired && (
          <Box as="span" color="red.500" ml={1}>
            *
          </Box>
        )}
      </FormLabel>
      <Popover placement="auto" strategy="fixed" closeOnBlur={false} isLazy>
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <InputGroup cursor="pointer">
                <Input
                  placeholder="Select Date"
                  value={value ? formatDateToDDMONTHNAMEYYYY(value) : ""}
                  readOnly
                  id={name}
                  cursor="pointer"
                />
                <InputRightElement pointerEvents="none">
                  <CalendarIcon color="gray.500" />
                </InputRightElement>
              </InputGroup>
            </PopoverTrigger>

            <PopoverContent
              zIndex={20000}
              maxW={{ base: "95vw", md: "520px" }}
              w="100%"
            >
              <PopoverArrow />
              <PopoverCloseButton />

              <PopoverBody>
                <Box
                  display="flex"
                  flexDir={{ base: "column", md: "row" }}
                  gap={4}
                >
                  {/* Presets */}
                  <SimpleGrid
                    columns={{ base: 2, md: 1 }}
                    spacing={2}
                    minW={{ md: "120px" }}
                  >
                    <Button
                      size="xs"
                      onClick={() => handleCustomDateChange(0)}
                      bg={
                        selectedPreset &&
                        selectedPreset.getTime() === new Date(today).getTime()
                          ? "blue.200"
                          : "transparent"
                      }
                    >
                      Today
                    </Button>
                    <Button
                      size="xs"
                      onClick={() => handleCustomDateChange(30)}
                      bg={
                        selectedPreset &&
                        selectedPreset.toDateString() ===
                          new Date(
                            today.getTime() + 30 * 24 * 60 * 60 * 1000
                          ).toDateString()
                          ? "blue.200"
                          : "transparent"
                      }
                    >
                      After 1 month
                    </Button>
                    <Button
                      size="xs"
                      onClick={() => handleCustomDateChange(90)}
                      bg={
                        selectedPreset &&
                        selectedPreset.toDateString() ===
                          new Date(
                            today.getTime() + 90 * 24 * 60 * 60 * 1000
                          ).toDateString()
                          ? "blue.200"
                          : "transparent"
                      }
                    >
                      After 3 months
                    </Button>
                    <Button
                      size="xs"
                      onClick={() => handleCustomDateChange(180)}
                      bg={
                        selectedPreset &&
                        selectedPreset.toDateString() ===
                          new Date(
                            today.getTime() + 180 * 24 * 60 * 60 * 1000
                          ).toDateString()
                          ? "blue.200"
                          : "transparent"
                      }
                    >
                      After 6 months
                    </Button>
                    <Button
                      size="xs"
                      onClick={() => handleCustomDateChange(360)}
                      bg={
                        selectedPreset &&
                        selectedPreset.toDateString() ===
                          new Date(
                            today.getTime() + 360 * 24 * 60 * 60 * 1000
                          ).toDateString()
                          ? "blue.200"
                          : "transparent"
                      }
                    >
                      After 1 year
                    </Button>
                  </SimpleGrid>

                  {/* Calendar */}
                  <Box flex="1" minW={0}>
                    <Box
                      as={Calendar}
                      onChange={onDateChange}
                      value={date}
                      className="react-calendar"
                      bg={calendarBg}
                      borderRadius="md"
                      borderColor={calendarBorderColor}
                      borderWidth="1px"
                      boxShadow="md"
                      sx={{
                        ".react-calendar__tile": {
                          padding: "5px",
                          bg: calendarBg,
                          borderRadius: "md",
                          border: "1px solid",
                          borderColor: calendarBorderColor,
                          _hover: { bg: "blue.50" },
                        },
                        ".react-calendar__tile--active": {
                          bg: "blue.500",
                          color: "white",
                          _hover: { bg: "blue.600" },
                        },
                        ".react-calendar__navigation button": {
                          color: "blue.500",
                          _hover: { bg: "blue.50" },
                          minWidth: "30px",
                          height: "30px",
                        },
                        ".react-calendar__navigation button:disabled": {
                          color: "gray.400",
                        },
                      }}
                    />

                    <HStack spacing={3} mt={3} justifyContent="center">
                      <Box textAlign="center">
                        <Text fontSize="xs" fontWeight="bold">
                          Selected:
                        </Text>
                        <Text fontSize="xs">
                          {date ? formatDateToDDMONTHNAMEYYYY(date) : "N/A"}
                        </Text>
                      </Box>
                    </HStack>

                    <Button
                      mt={3}
                      size="sm"
                      w="100%"
                      bg="blue.500"
                      color="white"
                      _hover={{ bg: "blue.600" }}
                      onClick={() => applyFilter(onClose)}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </PopoverBody>
            </PopoverContent>
          </>
        )}
      </Popover>
      <ErrorMessage error={error} />
    </Box>
  );
};

export default CalendarInput;
